/**
 * recordStatus.js — the lifecycle-status badge rule shared by the two records that have one.
 * Pure: no imports, no state, no React.
 *
 * A blob and a revision carry different status vocabularies, but they answer the same three
 * questions, so the rule is stated once here rather than copied beside each vocabulary. That is the
 * one thing this module does NOT share with its neighbours: scheduleStateBadgeClass
 * (blob_types/blobhub/compute/scheduler/schedules/badgeClass.js) and sessionStatusClass
 * (…/playground/session/sessionStatus.js) each live beside their own domain because each applies a
 * genuinely different rule — the scheduler splits two ways on `active`, the session splits on which
 * states are still open. Blob and revision split identically, and two places to write one fact is
 * one place too many.
 */

// api/data/blobs.py
export const BLOB_STATUS = {
  CREATING_BLOB: 'creating_blob',
  CREATING_REVISION: 'creating_revision',
  READY: 'ready',
  UPDATING: 'updating',
  DELETING: 'deleting',
  FAILED: 'failed'
};

// api/data/revisions.py. `phase` is a separate axis (draft / commit / snapshot / managed) and keeps
// the plain teal pill — it says where the revision sits in its lifecycle, not whether it is healthy.
export const REVISION_STATUS = {
  CREATING: 'creating',
  READY: 'ready',
  COMMITTING: 'committing',
  FAILED: 'failed',
  DELETING: 'deleting'
};

/**
 * The tone a status reads as — the one place the three-way split is decided.
 *
 * `ready` is the steady state. `failed` is the only value that asks for a person. Everything else —
 * creating, committing, updating, deleting — is transient: something is already happening and the
 * record will move on by itself, so it reads as neither healthy nor alarming.
 *
 * The default is 'transient' rather than 'ready' on purpose. A status either record grows later must
 * not silently inherit the healthy treatment just because nothing here has been taught about it yet.
 *
 * Returns a tone, not a class or an icon, so the one rule serves every presentation of it —
 * components/controls/statusIndicator.js turns a tone into a glyph and a colour.
 */
export function recordStatusTone(status) {
  if ( 'ready' === status ) return 'ready';
  if ( 'failed' === status ) return 'failed';
  return 'transient';
}
